import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Center,
  AvatarBadge,
  Text,
  Divider,
  Input,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Button,
  useToast,
  CircularProgress,
  Checkbox,
  Select,
} from '@chakra-ui/core';
import { AiOutlineCamera } from 'react-icons/ai';
import Joi from 'joi';
import { useTranslation } from 'react-i18next';
// import _isString from 'lodash/isString';
import i18next from 'i18next';
import colors from '../../../styles/colors';
import { errorFormat, FormattedError, getErrorMessage, hasErrorMessage, clearErrorMessage } from '../../../utils/FormErrorUtils';
import { profileStyle } from '../ProfileStyle';
import { useStoreActions, useStoreState } from '../../../models/hooks';
import { ProfilePicture } from '../../common';
import { UserProfileType } from '../ProfileTypes';
import { emailSchema } from '../../../utils/emailValidationUtils';
import { formatTimeZones, TimeZoneFormat } from '../../../app/constants/timezones';
import { getTranslatedValue } from '../../../utils/localizedString';
import { getDefaultTimeZoneForCountry, getTimeZoneOffset } from '../../../utils/timezoneUtils';
import { CustomFloatingField } from '../../../app/components/CustomFloatingField';
import useTimezoneEnabled from '../../../app/hooks/useTimezoneEnabled';
// import PreferenceDial from './PreferenceDial';
// import useMaxCandidateLimit from '../../../app/hooks/useMaxCandidateLimit';

const inputStyles = {
  height: '50px',
  paddingLeft: '15px',
  paddingTop: '20px',
};

const defaultUserProfile: UserProfileType = {
  firstName: '',
  lastName: '',
  email: '',
  primaryTimezone: {
    name: '',
    offset: '',
    label: '',
  },
  secondaryTimezone: {
    name: '',
    offset: '',
    label: '',
  },
  isSecondaryTimezoneEnabled: false,
};
type MyProfileViewProps = {
  picture: string | undefined;
  onClose: () => void;
};

export const MyProfileView = ({ picture, onClose }: MyProfileViewProps): JSX.Element => {
  const { t } = useTranslation('profile');
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { user } = useStoreState((s) => s.app);

  const isTimezoneEnabled = useTimezoneEnabled();

  const [userProfile, setUserProfile] = useState<UserProfileType>(defaultUserProfile);
  const [formErrors, setFormErrors] = useState<FormattedError[]>([]);
  const [updatingProfile, setUpdatingProfile] = useState<boolean>(false);
  const [updatingProfilePicture, setUpdatingProfilePicture] = useState<boolean>(false);
  const [profilePicture, setProfilePicture] = useState<string>(picture || '');

  const setLocalProfileImageUrl = useStoreActions((a) => a.app.setRecruiterProfileImage);
  const { updateProfile, uploadProfilePicture } = useStoreActions((actions) => actions.profileManagement);

  const [timeZones, setTimeZones] = useState<TimeZoneFormat[]>([]);
  const [primaryLabelfocused, setPrimaryLabelFocused] = useState<boolean>(false);
  const [primaryTzfocused, setPrimaryTzFocused] = useState<boolean>(false);
  const [secondaryLabelfocused, setSecondaryLabelFocused] = useState<boolean>(false);
  const [secondaryTzfocused, setSecondaryTzFocused] = useState<boolean>(false);

  const shouldFloatPrimaryLabel =
    primaryLabelfocused || (userProfile.primaryTimezone?.label?.length ?? /* istanbul ignore next */ 0) > 0;
  const shouldFloatPrimaryTz =
    primaryTzfocused || (userProfile.primaryTimezone?.name?.length ?? /* istanbul ignore next */ 0) > 0;
  const shouldFloatSecondaryLabel =
    secondaryLabelfocused || (userProfile.secondaryTimezone?.label?.length ?? /* istanbul ignore next */ 0) > 0;
  const shouldFloatSecondaryTz =
    secondaryTzfocused || (userProfile.secondaryTimezone?.name?.length ?? /* istanbul ignore next */ 0) > 0;

  useEffect(() => {
    if (user) {
      setUserProfile({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        primaryTimezone: user.primaryTimezone || { name: '', offset: '', label: '' },
        secondaryTimezone: user.secondaryTimezone || { name: '', offset: '', label: '' },
        isSecondaryTimezoneEnabled: user.isSecondaryTimezoneEnabled || false,
      });
    }
  }, [user]);

  const uploadTrigger = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target && event.target.files) {
      const file = event.target.files[0];
      const actFileSize = Math.round(file.size / 1024);

      if (!(file.type === 'image/png' || file.type === 'image/jpeg')) {
        toast({
          title: t('profileImageFormatFail'),
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
        // this line right below will reset the
        // input field so if you removed it you can re-add the same file
        /* istanbul ignore else */
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      if (actFileSize > 1024) {
        toast({
          title: t('profileImageSizeFail'),
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
        // this line right below will reset the
        // input field so if you removed it you can re-add the same file
        /* istanbul ignore else */
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      setProfilePicture(URL.createObjectURL(file));
      if (user) {
        const filename = `${user.id}.png`;
        try {
          setUpdatingProfilePicture(true);
          await uploadProfilePicture({ fileName: filename, profilePicture: file });
          setLocalProfileImageUrl(URL.createObjectURL(file));
          setUpdatingProfilePicture(false);
          toast({
            title: t('profilePictureUpdated'),
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
        } catch (e) {
          setLocalProfileImageUrl(picture || '');
          setProfilePicture(picture || '');
          setUpdatingProfilePicture(false);
          toast({
            title: t('profilePictureUpdateFail'),
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      }
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
    }
  };

  const schema = Joi.object()
    .options({ abortEarly: false })
    .keys({
      lastName: Joi.string()
        .trim()
        .required()
        .min(2)
        .max(30)
        .messages({
          'string.empty': t('lastNameRequired'),
          'string.min': t('lastNameMin'),
          'string.max': t('lastNameMax'),
        }),
      firstName: Joi.string()
        .trim()
        .required()
        .min(2)
        .max(30)
        .messages({
          'string.empty': t('firstNameRequired'),
          'string.min': t('firstNameMin'),
          'string.max': t('firstNameMax'),
        }),
      email: emailSchema(t),
      isSecondaryTimezoneEnabled: Joi.boolean(),

      primaryTimezone: Joi.object({
        name: Joi.string()
          .required()
          .messages({
            'string.empty': t('validationPrimaryTz'),
          }),
        label: Joi.string().when('...isSecondaryTimezoneEnabled', {
          is: true,
          then: Joi.required().messages({
            'string.empty': t('validationLabel'),
            'any.required': t('validationLabel'),
          }),
          otherwise: Joi.optional().allow(''),
        }),
        offset: Joi.string().allow(''),
      }),

      secondaryTimezone: Joi.object({
        name: Joi.string().when('...isSecondaryTimezoneEnabled', {
          is: true,
          then: Joi.required().messages({
            'string.empty': t('validationSecondaryTz'),
            'any.required': t('validationSecondaryTz'),
          }),
          otherwise: Joi.optional().allow(''),
        }),
        offset: Joi.string().allow(''),
        label: Joi.string().when('...isSecondaryTimezoneEnabled', {
          is: true,
          then: Joi.required().messages({
            'string.empty': t('validationLabel'),
            'any.required': t('validationLabel'),
          }),
          otherwise: Joi.optional().allow(''),
        }),
      }),
    });

  const formValidation = (formData: UserProfileType) => {
    const { error } = schema.validate(formData);
    if (error) {
      setFormErrors(errorFormat(error.details));
      return false;
    }

    setFormErrors([]);
    return true;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;

    setUserProfile((prev) => ({
      ...prev,
      [id]: value,
    }));

    setFormErrors((prev) => clearErrorMessage(prev, id));
  };

  const onUpdateProfile = async () => {
    const validate = formValidation(userProfile);

    if (validate && user) {
      try {
        setUpdatingProfile(true);

        const { isSecondaryTimezoneEnabled, secondaryTimezone, ...rest } = userProfile;

        const payload = {
          userID: user.id,
          userProfile: {
            ...rest,
            isSecondaryTimezoneEnabled,
            ...(isSecondaryTimezoneEnabled && { secondaryTimezone }),
          },
        };

        await updateProfile(payload);
        setUpdatingProfile(false);
        toast({
          title: t('profileUpdatedSuccessfully'),
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        onClose();
      } catch (e) {
        setUpdatingProfile(false);
        toast({
          title: t('profileUpdatedFailed'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const getTimeZonesOfCountry = (): TimeZoneFormat[] => {
    return formatTimeZones;
  };

  useEffect(() => {
    const tzs = getTimeZonesOfCountry();
    setTimeZones(tzs);

    if (user) {
      const defaultTZ = getDefaultTimeZoneForCountry(user?.countryCode);

      setUserProfile((prev) => ({
        ...prev,
        primaryTimezone: prev.primaryTimezone?.name
          ? prev.primaryTimezone // keep existing
          : {
              name: defaultTZ,
              label: '',
              offset: getTimeZoneOffset(defaultTZ),
            },
      }));
    }
  }, [user]);

  const formattedTimeZoneOptions = timeZones.map((tz) => ({
    value: tz.value,
    label: getTranslatedValue(tz.label, i18next.language),
  }));

  return (
    <Box css={profileStyle}>
      <Center>
        <ProfilePicture
          data-testid="UserProfilePicture"
          name={userProfile?.firstName}
          style={{ backgroundColor: colors.blue.default, color: colors.white }}
          src={profilePicture}
          width="80px"
          height="80px"
          className="profilePicture"
          onClick={() => {
            /* istanbul ignore else */
            if (!updatingProfilePicture) {
              fileInputRef.current?.click();
            }
          }}
        >
          <AvatarBadge boxSize="2em" bg={colors.profile.profileBatchColor}>
            {updatingProfilePicture ? (
              <CircularProgress isIndeterminate />
            ) : (
              <AiOutlineCamera color={colors.profile.cameraIconColor} />
            )}
          </AvatarBadge>
        </ProfilePicture>
        <Box display="none">
          <input type="file" onChange={uploadTrigger} ref={fileInputRef} accept="image/*" data-testid="profileImageInput" />
        </Box>
      </Center>

      <Center>
        <Text paddingTop="1rem" fontSize="14px" fontWeight={700} color={colors.orange[500]}>
          {`${userProfile.firstName} ${userProfile.lastName}`}
        </Text>
      </Center>
      <Box marginTop="1rem">
        <Text fontSize={{ base: '14px', md: '16px' }} fontWeight={700}>
          {t('personalInformation')}
        </Text>
        <Divider />
        <Flex>
          <Box paddingTop="1rem" w="50%" marginRight=".5rem">
            <FormControl isInvalid={hasErrorMessage(formErrors, 'firstName')} isRequired>
              <FormLabel htmlFor="address" fontSize="15px" fontWeight={400}>
                {t('firstName')}
              </FormLabel>
              <Input
                data-testid="firstNameTextField"
                variant="filled"
                id="firstName"
                type="text"
                size="md"
                value={userProfile.firstName}
                onChange={handleChange}
              />
              <FormErrorMessage>{getErrorMessage(formErrors, 'firstName')}</FormErrorMessage>
            </FormControl>
          </Box>
          <Box paddingTop="1rem" w="50%" marginLeft=".5rem">
            <FormControl isInvalid={hasErrorMessage(formErrors, 'lastName')} isRequired>
              <FormLabel htmlFor="address" fontSize="15px" fontWeight={400}>
                {t('lastName')}
              </FormLabel>
              <Input
                data-testid="lastNameTextField"
                variant="filled"
                id="lastName"
                type="text"
                size="md"
                value={userProfile.lastName}
                onChange={handleChange}
              />
              <FormErrorMessage>{getErrorMessage(formErrors, 'lastName')}</FormErrorMessage>
            </FormControl>
          </Box>
        </Flex>
        <Flex>
          <Box paddingTop="1rem" w="50%" marginRight=".5rem">
            <FormControl isInvalid={hasErrorMessage(formErrors, 'email')} isRequired>
              <FormLabel htmlFor="address" fontSize="15px" fontWeight={400}>
                {t('email')}
              </FormLabel>
              <Input
                data-testid="emailNameTextField"
                variant="filled"
                id="email"
                type="text"
                size="md"
                value={userProfile.email}
                onChange={handleChange}
              />
              <FormErrorMessage>{getErrorMessage(formErrors, 'email')}</FormErrorMessage>
            </FormControl>
          </Box>
          <Box paddingTop="1rem" w="50%" marginLeft=".5rem" />
        </Flex>
      </Box>

      {isTimezoneEnabled && (
        <Box marginTop="1rem">
          <Text fontSize={{ base: '14px', md: '16px' }} fontWeight={700}>
            {t('timezone')}
          </Text>
          <Divider />

          <Checkbox
            mt={4}
            isChecked={userProfile.isSecondaryTimezoneEnabled}
            fontSize="sm"
            onChange={(e) => {
              const isChecked = e.target.checked;
              setUserProfile((prev) => ({
                ...prev,
                isSecondaryTimezoneEnabled: isChecked,
                secondaryTimezone: isChecked ? prev.secondaryTimezone : { name: '', label: '', offset: '' }, // reset secondary timezone if unchecked
              }));

              // clear errors when unchecked
              if (!isChecked) {
                setFormErrors((prev) =>
                  prev.filter(
                    (err) =>
                      err.label !== 'primaryTimezone.label' &&
                      err.label !== 'secondaryTimezone.name' &&
                      err.label !== 'secondaryTimezone.label',
                  ),
                );
              }
            }}
            data-testid="secondaryTimezoneCheckbox"
          >
            {t('displaySecondaryTimezone')}
          </Checkbox>

          <Flex width="100%" alignItems="baseline" marginTop="1rem">
            <FormControl width="70%">
              <CustomFloatingField label={t('primaryTimezone')} htmlLabel="primary-timezone" shouldFloat={shouldFloatPrimaryTz}>
                <Select
                  value={userProfile.primaryTimezone?.name || ''}
                  id="primary-timezone"
                  data-testid="PrimaryTimezoneSelect"
                  onFocus={() => setPrimaryTzFocused(true)}
                  onBlur={() => setPrimaryTzFocused(false)}
                  onChange={(e) => {
                    const tzName = e.target.value;
                    setUserProfile((prev) => ({
                      ...prev,
                      primaryTimezone: {
                        ...prev.primaryTimezone,
                        name: tzName,
                        offset: getTimeZoneOffset(tzName),
                      },
                    }));

                    setFormErrors((prev) => clearErrorMessage(prev, 'primaryTimezone.name'));
                  }}
                  fontSize="15px"
                  backgroundColor={colors.input.background}
                  _placeholder={{ color: colors.input.placeholderColor }}
                  style={inputStyles}
                >
                  <option value="" selected disabled>
                    {t('selectTimezonePlaceholder')}
                  </option>
                  {formattedTimeZoneOptions.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </Select>
              </CustomFloatingField>
            </FormControl>

            <FormControl isInvalid={hasErrorMessage(formErrors, 'primaryTimezone.label')} width="30%" ml={4}>
              <CustomFloatingField label={t('label')} htmlLabel="primary-label" shouldFloat={shouldFloatPrimaryLabel}>
                <Input
                  data-testid="primaryLabelField"
                  variant="filled"
                  id="label"
                  type="text"
                  size="md"
                  maxLength={5}
                  value={userProfile.primaryTimezone?.label || ''}
                  onFocus={() => setPrimaryLabelFocused(true)}
                  onBlur={() => setPrimaryLabelFocused(false)}
                  onChange={
                    /* istanbul ignore next */ (e) => {
                      const label = e.target.value;
                      setUserProfile((prev) => ({
                        ...prev,
                        primaryTimezone: {
                          ...prev.primaryTimezone,
                          label,
                        },
                      }));

                      setFormErrors((prev) => clearErrorMessage(prev, 'primaryTimezone.label'));
                    }
                  }
                  style={inputStyles}
                />
              </CustomFloatingField>
              <FormErrorMessage>{getErrorMessage(formErrors, 'primaryTimezone.label')}</FormErrorMessage>
            </FormControl>
          </Flex>

          <Flex width="100%" alignItems="baseline" marginTop="15px">
            <FormControl isInvalid={hasErrorMessage(formErrors, 'secondaryTimezone.name')} width="70%">
              <CustomFloatingField
                label={t('secondaryTimezone')}
                htmlLabel="secondary-timezone"
                shouldFloat={shouldFloatSecondaryTz}
              >
                <Select
                  value={userProfile.secondaryTimezone?.name || ''}
                  data-testid="SecondaryTimezoneSelect"
                  id="secondary-timezone"
                  onFocus={() => setSecondaryTzFocused(true)}
                  onBlur={() => setSecondaryTzFocused(false)}
                  onChange={
                    /* istanbul ignore next */ (e) => {
                      const tzValue = e.target.value;
                      setUserProfile((prev) => ({
                        ...prev,
                        secondaryTimezone: {
                          ...prev.secondaryTimezone,
                          name: tzValue,
                          offset: getTimeZoneOffset(tzValue),
                        },
                      }));

                      setFormErrors((prev) => clearErrorMessage(prev, 'secondaryTimezone.name'));
                    }
                  }
                  fontSize="15px"
                  backgroundColor={colors.input.background}
                  isDisabled={!userProfile.isSecondaryTimezoneEnabled}
                  _placeholder={{ color: colors.input.placeholderColor }}
                  style={inputStyles}
                >
                  {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                  <option value="" disabled />

                  {formattedTimeZoneOptions.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </Select>
              </CustomFloatingField>
              <FormErrorMessage>{getErrorMessage(formErrors, 'secondaryTimezone.name')}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={hasErrorMessage(formErrors, 'secondaryTimezone.label')} width="30%" ml={4}>
              <CustomFloatingField label={t('label')} htmlLabel="secondary-label" shouldFloat={shouldFloatSecondaryLabel}>
                <Input
                  data-testid="secondaryLabelField"
                  variant="filled"
                  id="label"
                  type="text"
                  size="md"
                  maxLength={5}
                  value={userProfile.secondaryTimezone?.label || ''}
                  onFocus={() => setSecondaryLabelFocused(true)}
                  onBlur={() => setSecondaryLabelFocused(false)}
                  onChange={(e) => {
                    const label = e.target.value;
                    setUserProfile((prev) => ({
                      ...prev,
                      secondaryTimezone: {
                        ...prev.secondaryTimezone,
                        label,
                      },
                    }));

                    setFormErrors((prev) => clearErrorMessage(prev, 'secondaryTimezone.label'));
                  }}
                  isDisabled={!userProfile.isSecondaryTimezoneEnabled}
                  style={inputStyles}
                />
              </CustomFloatingField>
              <FormErrorMessage>{getErrorMessage(formErrors, 'secondaryTimezone.label')}</FormErrorMessage>
            </FormControl>
          </Flex>
        </Box>
      )}

      <Flex justifyContent="end" marginTop="1rem">
        <Button data-testid="updateProfileBtn" colorScheme="blue" isLoading={updatingProfile} onClick={onUpdateProfile}>
          {t('update')}
        </Button>
      </Flex>
    </Box>
  );
};
